import { getCommercialBOMList } from "@erp/shared/api/generated/wrappers/product";
import { showConfirm } from "@erp/shared/utils/dialog";
import { computed, ref } from "vue";
import { PRODUCT_TYPE } from "@erp/shared/types/product.types";
/**
 * @description 재료비 관리 컴포저블
 */
export const useStockCost = () => {
    const selectedIds = ref([]);
    const selectedPart = ref(null);
    const stockCosts = ref([]);
    const bomPath = ref([]);
    // --- Setters ---
    const setSelectedIds = (ids) => {
        selectedIds.value = ids;
    };
    const setStockCosts = (costs) => {
        stockCosts.value = costs;
    };
    const setSelectedPart = (part) => {
        selectedPart.value = part;
        bomPath.value = [];
    };
    // --- 드릴다운 경로를 따라 현재 보고 있는 BOM 노드 ---
    const currentBomPart = computed(() => {
        if (!selectedPart.value)
            return null;
        const root = stockCosts.value.find((sc) => sc.id === selectedPart.value.id);
        if (!root)
            return null;
        let current = root;
        for (const pathId of bomPath.value) {
            const child = (current.children ?? []).find((c) => c.id === pathId);
            if (!child)
                return null;
            current = child;
        }
        return current;
    });
    // --- 브레드크럼 네비게이션 ---
    const bomBreadcrumbs = computed(() => {
        if (!selectedPart.value)
            return [];
        const root = stockCosts.value.find((sc) => sc.id === selectedPart.value.id);
        if (!root)
            return [];
        const crumbs = [root];
        let current = root;
        for (const pathId of bomPath.value) {
            const child = (current.children ?? []).find((c) => c.id === pathId);
            if (!child)
                break;
            crumbs.push(child);
            current = child;
        }
        return crumbs;
    });
    const drillIntoPart = (part) => {
        bomPath.value = [...bomPath.value, part.id];
    };
    const goBackToLevel = (level) => {
        bomPath.value = bomPath.value.slice(0, level);
    };
    const isAddableBOM = computed(() => {
        return (selectedPart.value && selectedPart.value.type !== PRODUCT_TYPE.원부자재);
    });
    /**
     * @description 현재 보고 있는 BOM 노드에 하위 제품 추가 (제품 마스터 검색)
     */
    const addSubPart = (products, quantityRecord) => {
        const target = currentBomPart.value;
        if (!target)
            return;
        if (!target.children)
            target.children = [];
        products.forEach((product) => {
            target.children.push({
                id: product.id,
                type: product.type,
                classificationId: product.classificationId,
                manufacturerId: product.manufacturerId,
                productCode: product.productCode,
                classification: product.classification,
                name: product.name,
                model: product.model,
                spec: product.spec,
                manufacturer: product.manufacturer,
                directlyWritten: product.directlyWritten,
                quantity: quantityRecord?.[product.id] ?? 1,
                quantityUnit: product.quantityUnit,
                images: product.images,
                children: [],
            });
        });
    };
    /**
     * @description 현재 보고 있는 BOM 노드에 직접 입력으로 하위 제품 추가
     */
    const addDirectInputSubPart = (itemData) => {
        const target = currentBomPart.value;
        if (!target)
            return;
        if (!target.children)
            target.children = [];
        const tempId = -(Date.now() + Math.random());
        target.children.push({
            id: tempId,
            type: itemData.type,
            classificationId: null,
            manufacturerId: null,
            productCode: `TEMP-${Date.now()}`,
            classification: null,
            name: itemData.name,
            model: itemData.model,
            spec: itemData.spec,
            manufacturer: itemData.manufacturer ?? null,
            directlyWritten: 1,
            quantity: itemData.quantity,
            quantityUnit: itemData.quantityUnit,
            images: [],
            children: [],
        });
    };
    /**
     * @description 제품 마스터 검색으로 최상위 BOM 항목(재료) 추가
     */
    const addStockCostFromProducts = (products, quantityRecord) => {
        products.forEach((product) => {
            stockCosts.value.push({
                id: product.id,
                type: product.type,
                classificationId: product.classificationId,
                manufacturerId: product.manufacturerId,
                productCode: product.productCode,
                classification: product.classification,
                name: product.name,
                model: product.model,
                spec: product.spec,
                manufacturer: product.manufacturer,
                directlyWritten: product.directlyWritten,
                quantity: quantityRecord?.[product.id] ?? 1,
                quantityUnit: product.quantityUnit,
                images: product.images,
                children: [],
            });
            selectedIds.value.push(product.id);
        });
    };
    /**
     * @description 직접 입력으로 최상위 BOM 항목(재료) 추가
     */
    const addDirectInputStockCost = (itemData) => {
        const tempId = -(Date.now() + Math.random());
        stockCosts.value.push({
            id: tempId,
            type: itemData.type,
            classificationId: null,
            manufacturerId: null,
            productCode: `TEMP-${Date.now()}`,
            classification: null,
            name: itemData.name,
            model: itemData.model,
            spec: itemData.spec,
            manufacturer: itemData.manufacturer ?? null,
            directlyWritten: 1,
            quantity: itemData.quantity,
            quantityUnit: itemData.quantityUnit,
            images: [],
            children: [],
        });
        selectedIds.value.push(tempId);
    };
    /**
     * @description 현재 보고 있는 BOM 노드의 하위 제품 수정
     */
    const editSubPart = (itemToEdit) => {
        const target = currentBomPart.value;
        if (!target || !itemToEdit)
            return;
        const subs = target.children ?? [];
        const subIdx = subs.findIndex((s) => s.id === itemToEdit.id);
        if (subIdx === -1)
            return;
        subs.splice(subIdx, 1, { ...subs[subIdx], ...itemToEdit });
    };
    /**
     * @description 최상위 직접 입력 BOM 항목 수정
     */
    const editDirectInputStockCost = (id, itemData) => {
        const idx = stockCosts.value.findIndex((item) => item.id === id);
        if (idx === -1)
            return;
        stockCosts.value.splice(idx, 1, {
            ...stockCosts.value[idx],
            name: itemData.name,
            model: itemData.model,
            spec: itemData.spec,
            type: itemData.type,
            quantity: itemData.quantity,
            quantityUnit: itemData.quantityUnit,
            manufacturer: itemData.manufacturer ?? null,
        });
    };
    /**
     * @description 현재 보고 있는 BOM 노드의 직접 입력 하위 제품 수정
     */
    const editDirectInputSubPart = (id, itemData) => {
        const target = currentBomPart.value;
        if (!target)
            return;
        const subs = target.children ?? [];
        const subIdx = subs.findIndex((s) => s.id === id);
        if (subIdx === -1)
            return;
        subs.splice(subIdx, 1, {
            ...subs[subIdx],
            name: itemData.name,
            model: itemData.model,
            spec: itemData.spec,
            type: itemData.type,
            quantity: itemData.quantity,
            quantityUnit: itemData.quantityUnit,
            manufacturer: itemData.manufacturer ?? null,
        });
    };
    /**
     * @description 현재 보고 있는 BOM 노드의 하위 제품 삭제
     */
    const deleteSubPart = async (subItem) => {
        const target = currentBomPart.value;
        if (!target)
            return;
        const ok = await showConfirm("정말 이 하위 제품을 BOM LIST에서 삭제하시겠습니까?", "BOM 삭제", false, "삭제", "취소");
        if (ok) {
            const subs = target.children ?? [];
            const subIdx = subs.findIndex((s) => s.id === subItem.id);
            if (subIdx !== -1)
                subs.splice(subIdx, 1);
        }
    };
    /**
     * @description 외부 데이터 적용
     */
    const applyStockCosts = (data) => {
        const parsedBomJson = data.bom?.bomJson && data.bom.bomJson.length > 0 ? data.bom.bomJson : [];
        const parsedMaterialIds = parsedBomJson.map((item) => item.id);
        selectedPart.value = null;
        bomPath.value = [];
        selectedIds.value = parsedMaterialIds;
        stockCosts.value = parsedBomJson;
    };
    /**
     * @description 재료비 삭제
     */
    const deleteStockCost = (id) => {
        const newData = stockCosts.value.filter((item) => item.id !== id);
        selectedIds.value = newData.map((item) => item.id);
        stockCosts.value = newData;
        if (selectedPart.value && selectedPart.value.id === id) {
            selectedPart.value = null;
            bomPath.value = [];
        }
    };
    /**
     * @description 재료비 조회
     */
    const fetchStockCosts = async (ids, unitPriceMap) => {
        try {
            const prevChildrenMap = new Map();
            stockCosts.value.forEach((item) => {
                if (item.children && item.children.length > 0) {
                    prevChildrenMap.set(item.id, item.children);
                }
            });
            selectedIds.value = ids;
            const bomList = await Promise.all(ids.map((id) => getCommercialBOMList(id)));
            const newList = bomList.flat();
            newList.forEach((item) => {
                const prevChildren = prevChildrenMap.get(item.id);
                if (prevChildren) {
                    item.children = prevChildren;
                }
                if (unitPriceMap?.has(item.id)) {
                    item.unitPrice = unitPriceMap.get(item.id);
                }
            });
            stockCosts.value = newList;
        }
        catch (error) {
            throw error instanceof Error ? error : new Error(String(error));
        }
    };
    return {
        selectedIds,
        setSelectedIds,
        stockCosts,
        setStockCosts,
        isAddableBOM,
        applyStockCosts,
        deleteStockCost,
        deleteSubPart,
        editSubPart,
        editDirectInputStockCost,
        editDirectInputSubPart,
        addSubPart,
        addDirectInputSubPart,
        addDirectInputStockCost,
        addStockCostFromProducts,
        selectedPart,
        setSelectedPart,
        fetchStockCosts,
        currentBomPart,
        bomBreadcrumbs,
        bomPath,
        drillIntoPart,
        goBackToLevel,
    };
};
